import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { enhancePrompt, type EnhancePhotoRef } from '../../../api/prompts'
import { assetNamesToTokens, assetTokensToNames, mentionAliases } from '../../../utils/assetMentions'

const DEV_MOCK = false
const DEV_MOCK_RESULT =
	'A cinematic close-up of a majestic snow leopard perched on ancient stone ruins at golden hour, soft bokeh background, dramatic lighting, ultra-detailed fur texture, 8k photography'

export type AutoPromptState = 'idle' | 'loading' | 'animating'

interface UseAutoPromptOptions {
	prompt: string
	modelId: string
	isVideo: boolean
	/** Человеческое имя нейросети — чтобы LLM не принял id за сюжет. */
	modelName?: string
	/** Приложенные фото на момент клика (getter — список живёт в баре ниже хука). */
	getPhotos?: () => EnhancePhotoRef[]
	/** Имена ассетов сцены по id: улучшатель видит «@Анна» вместо токена @{uuid}@, ответ возвращается чипами. */
	getAssetNames?: () => Record<string, string>
}

export function useAutoPrompt({ prompt, modelId, isVideo, modelName, getPhotos, getAssetNames }: UseAutoPromptOptions) {
	const { t } = useTranslation()
	const [state, setState] = useState<AutoPromptState>('idle')
	const [error, setError] = useState<string | null>(null)

	const enhancedRef = useRef<string>('')

	const isActive = prompt.trim().length > 0

	const run = useCallback(async () => {
		if (!isActive || state !== 'idle') return

		setError(null)
		setState('loading')

		try {
			// Только ассеты этого промпта и каждому своё имя — см. mentionAliases.
			const names = mentionAliases(prompt, getAssetNames?.() ?? {})
			const enhanced = DEV_MOCK
				? await new Promise<string>((res) => setTimeout(() => res(DEV_MOCK_RESULT), 700))
				: await enhancePrompt(assetTokensToNames(prompt.trim(), names), modelId, isVideo ? 'video' : 'image', {
						modelName,
						photos: getPhotos?.(),
					})

			enhancedRef.current = assetNamesToTokens(enhanced, names)
			setState('animating')
		} catch (err: unknown) {
			setState('idle')
			setError(err instanceof Error ? err.message : t('generate.error.enhanceFailed'))
			setTimeout(() => setError(null), 3000)
		}
	}, [isActive, state, prompt, modelId, isVideo, modelName, getPhotos, getAssetNames, t])

	const finishAnimation = useCallback(() => setState('idle'), [])

	return { state, error, isActive, run, finishAnimation, enhancedRef }
}
